import { useLingui } from "@lingui/react/macro";
import { ChevronRightIcon, type LucideIcon } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";

import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/components/ui-kit/base/popover";
import { Tooltip } from "@/components/ui-kit/base/tooltip";
import { cn } from "@/utils/shadcn-ui";

/** A single entry in a navigation menu. Structure only — presentation is the
 * renderer's job. */
export interface MenuItem {
  to: string;
  /** Display-ready text: static titles arrive already translated. */
  label: string;
  /** Shown on the level-1 rail and on the level-2 panel's own top level, which
   * needs one to survive the panel's icon-only state. Rows nested below that go
   * without. */
  icon?: LucideIcon;
  /** Hide the item without removing it from the definition. Defaults to shown. */
  show?: boolean;
  /** Expand the children by default (only relevant when `children` is set). */
  defaultExpanded?: boolean;
  /** Brand dot after the label: unfinished work waiting on that page. */
  dot?: string;
  children?: MenuItem[];
}

/** Row height inside its animating clip container, per the handoff. */
const ROW_CLIP_HEIGHT = 34;

/**
 * Gap between the sidebar's edge and the tooltip standing in for the footer
 * toggle's hidden label. The icon already ends some way short of that edge, so
 * the visible gap is that padding plus this. 0 sits the tooltip just clear of
 * the sidebar; Radix still adds ~2px of its own.
 */
export const NAV_TOOLTIP_OFFSET = 0;

/**
 * Level 2: the panel's page list, with deeper levels revealed inline rather
 * than in a third sliding panel.
 *
 * Rows animate their clip container's height, so a branch opening does not jump
 * the list. Unlike the handoff prototype, a parent row both navigates and
 * expands — every parent here (Alerts, Clinical studies) is a real page, and
 * toggle-only would make those routes unreachable from the menu. The chevron
 * still toggles without navigating.
 */
export function LevelTwoMenu({
  items,
  level = 0,
  activeItem,
  pathname,
  visible = true,
  collapsed = false,
}: {
  items: MenuItem[];
  level?: number;
  activeItem?: MenuItem;
  /** The URL to highlight against, in place of the live one — for a panel
   * still showing a section you have just left. */
  pathname?: string;
  /** False while an ancestor is collapsed — rows stay mounted but clip to 0. */
  visible?: boolean;
  /** Panel is an icon-only strip: labels fade out and branches close (see
   * LevelTwoRow). */
  collapsed?: boolean;
}) {
  const livePathname = useActivePathname();
  const activePathname = pathname ?? livePathname;
  const shown = items.filter((item) => item.show !== false);
  if (shown.length === 0) {
    return null;
  }

  const active = activeItem ?? findActiveItem(items, activePathname);

  return (
    <>
      {shown.map((item) => (
        <LevelTwoRow
          key={`${item.to}:${item.label}`}
          item={item}
          level={level}
          activeItem={active}
          pathname={activePathname}
          visible={visible}
          collapsed={collapsed}
        />
      ))}
    </>
  );
}

function LevelTwoRow({
  item,
  level,
  activeItem,
  pathname: activePathname,
  visible,
  collapsed,
}: {
  item: MenuItem;
  level: number;
  activeItem?: MenuItem;
  pathname: string;
  visible: boolean;
  collapsed: boolean;
}) {
  const { t } = useLingui();
  const flyout = useNavFlyout();
  const flyoutId = `level-two:${item.to}:${item.label}`;
  const childItems = (item.children ?? []).filter((c) => c.show !== false);
  const expandable = childItems.length > 0;
  const containsActiveRoute = expandable && isUnder(item.to, activePathname);
  const [expanded, setExpanded] = useState(
    item.defaultExpanded ?? containsActiveRoute,
  );

  // Auto-expand when the active route falls under this item. Not keyed on
  // `expanded`, so a manual collapse isn't immediately undone — it only
  // re-expands when the active route changes.
  useEffect(() => {
    if (containsActiveRoute) {
      setExpanded(true);
    }
  }, [containsActiveRoute]);

  const Icon = item.icon;
  const isCurrent = item === activeItem;
  // A parent takes the highlight only while it is closed over its selected
  // child, so the branch never shows two highlighted rows at once. Shrinking
  // the panel closes every branch, so it counts as closed here too — otherwise
  // the strip would show no current row at all on a nested route.
  const highlight =
    isCurrent || (containsActiveRoute && (!expanded || collapsed));
  // The same for every level: nesting is already spelled by the icon box, which
  // a nested row reserves but draws nothing in, so its label lands in the same
  // column as its parent's rather than stepping further right each level. Adding
  // to it as well pushed sub-items out past their parent's text.
  //
  // Unchanged by the panel shrinking, too: the list is pinned at the panel's
  // expanded width (see app-sidebar.tsx) so rows never re-flow, exactly as the
  // rail pins its own column. That keeps the selected row's 8px inset while the
  // panel narrows — flush against the rail it read as a bug — and holds the icon
  // box on 18–38px, still centred on 28 in the 56px strip.
  const indent = 8;

  // Same duration and curve as the panel's own width and the label fades below,
  // so a branch opening or the panel narrowing read as one movement rather than
  // several ending at different times.
  const clip = (children: React.ReactNode) => (
    <div
      className="overflow-hidden transition-[height,opacity] duration-280 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none"
      style={{
        height: visible ? ROW_CLIP_HEIGHT : 0,
        opacity: visible ? 1 : 0,
      }}
    >
      {children}
    </div>
  );

  return (
    <>
      {clip(
        // Named group so the row's hover survives the pointer moving onto the
        // expand chevron, which is a sibling of the link — see rail-menu.tsx.
        <div
          className="group/level-two-row relative"
          style={{ marginLeft: indent }}
          {...flyout.triggerProps(flyoutId, collapsed)}
        >
          <Link
            to={item.to}
            aria-current={isCurrent ? "page" : undefined}
            className={cn(
              // Every row runs flush into the sidebar's right edge and is
              // rounded only on the left, so both the hover and the selected
              // wash read as continuing past the edge rather than as a pill
              // floating in the panel — the cropped shape a level-1 row takes
              // once the rail collapses over it. One shared box for both states,
              // rather than insetting the idle row: changing the margin on hover
              // would shift the text sideways under the pointer.
              // 140ms plain `ease`, matching the level-1 rows' hover.
              // pl-[7px]: with the row's 8px indent and 3px border that puts
              // the icon box on 18–38px, the column the rail's icons sit in
              // (its list px-2 8 + row px-2.5 10). One value for both states —
              // the row keeps its width, so there is nothing to re-derive when
              // the panel narrows.
              // pr-3 rather than clearing the expand chevron for every row:
              // only the few branch rows carry one, and their labels are short
              // enough to stop well before it, so the longest labels get the
              // width instead.
              "flex h-[33px] items-center gap-1.5 rounded-l-[7px] border-l-[3px] pr-3 pl-[7px] tracking-[-0.005em] whitespace-nowrap transition-colors duration-140 ease-[ease] hover:no-underline",
              // Not on the current row — see the same gate on the rail's rows.
              !highlight && "group-hover/level-two-row:bg-sidebar-accent",
              level === 0 ? "text-[13px]" : "text-[12.5px]",
              // Transparent border on inactive rows so the text never shifts
              // sideways when a row becomes active.
              highlight
                ? "border-l-brand-primary bg-nav-active text-nav-active-foreground font-medium"
                : "text-sidebar-foreground border-l-transparent",
            )}
          >
            {/* Same fixed 20px box as the rail's rows, so the glyph never
                shifts — neither between the panel's expanded and icon-only
                states nor when a row becomes active. 16px glyph, not the
                rail's 19px: these rows are 33px tall, not 40px.
                Only the panel's own top level draws one: a glyph per row down
                every branch reads as a list of peers rather than a hierarchy,
                and nested rows never reach the icon-only strip anyway (see the
                recursion below). The box stays reserved for them, so their
                labels keep the indent the nesting is there to show instead of
                sliding back under their parent's. */}
            <span className="flex w-5 flex-none items-center justify-center">
              {level === 0 && Icon && (
                <Icon
                  className={cn(
                    "size-4 transition-colors duration-160 ease-[ease]",
                    highlight ? "text-nav-active-foreground" : "text-nav-icon",
                  )}
                  strokeWidth={1.7}
                />
              )}
            </span>

            {/* Exactly the rail labels' treatment: it fades and slides out
                  rather than unmounting, so the icon beside it never moves and
                  the whole sidebar's text leaves as one movement. The slide also
                  carries it clear of the strip's edge, so no sliver of a label
                  peeks out of the 56px. */}
            <span
              className={cn(
                "min-w-0 flex-1 truncate transition-[opacity,transform] duration-280 ease-[cubic-bezier(.4,0,.2,1)] motion-reduce:transition-none",
                collapsed && "opacity-0 [transform:translateX(-6px)]",
              )}
            >
              {item.label}
            </span>
          </Link>

          {/* Outside the link, so the dot doesn't end up in its accessible name.
              Expanded, it takes the chevron's place — no dotted item is a
              branch — centred on the same point: the chevron's 20px box sits at
              `right-3`, so an 8px dot at 12 + (20 − 8) / 2 = 18px. In the
              icon-only strip it moves onto the glyph's top-right corner instead
              of hiding with the label, since the strip is the default state and
              the dot is the only reminder of an unsaved draft. There the
              panel-coloured ring cuts it out of the glyph, and it lets the
              pointer through to the link it sits over. */}
          {item.dot && (
            <span
              role="img"
              aria-label={item.dot}
              title={item.dot}
              className={cn(
                "bg-brand-primary absolute size-2 rounded-full",
                collapsed
                  ? "ring-nav-panel pointer-events-none top-[5px] left-[23px] ring-2"
                  : "top-1/2 right-[18px] -translate-y-1/2",
              )}
            />
          )}

          {/* Stands in for the label whenever the strip has hidden it. A branch
              closes while the panel is a strip, so the flyout carries its pages
              too — the only way to reach them without expanding the sidebar.
              The row starts 8px into a 56px strip, so a 48px anchor reaches the
              strip's edge, the same as a rail row. */}
          {collapsed && (
            <NavFlyout
              id={flyoutId}
              entries={[
                { item, current: isCurrent },
                ...childItems.map((child) => ({
                  item: child,
                  current: child === activeItem,
                  nested: true,
                })),
              ]}
              width="w-12"
              rowHeight={33}
            />
          )}

          {/* It never turns: branches close in the strip, so there is no open
              state to show, even with one of the pages current. */}
          {expandable && collapsed && (
            <IconOnlyChevron active={highlight} className="size-[13px]" />
          )}

          {expandable && !collapsed && (
            <button
              type="button"
              aria-label={expanded ? t`Collapse` : t`Expand`}
              aria-expanded={expanded}
              onClick={() => setExpanded((value) => !value)}
              className={cn(
                "absolute top-1/2 right-3 flex size-5 -translate-y-1/2 cursor-pointer items-center justify-center border-none bg-transparent p-0",
                // The row's ink, like its icon and label. No hover change on
                // the current row, for the reason its wash has none.
                highlight
                  ? "text-nav-active-foreground"
                  : "text-nav-icon hover:text-sidebar-accent-foreground",
              )}
            >
              <ChevronRightIcon
                className={cn(
                  "size-[13px] transition-transform duration-180 ease-[ease]",
                  expanded && "rotate-90",
                )}
                strokeWidth={2.2}
              />
            </button>
          )}
        </div>,
      )}

      {expandable && (
        <LevelTwoMenu
          items={childItems}
          level={level + 1}
          activeItem={activeItem}
          pathname={activePathname}
          // Every branch closes while the panel is a strip: a nested row has
          // no icon to stand in for its label, so it has nothing to show there.
          // Expanding the panel restores whatever was open before.
          visible={visible && expanded && !collapsed}
          collapsed={collapsed}
        />
      )}
    </>
  );
}

/** Pathname used to decide the active menu item. Routes that live off the main
 * nav (e.g. a patient's Settings page) pass the tab they were opened from as
 * `location.state.settingsFrom`, so that origin tab stays highlighted instead
 * of the page collapsing onto its closest ancestor. Without it we use the URL. */
export function useActivePathname(): string {
  const location = useLocation();
  const settingsFrom = (location.state as { settingsFrom?: string } | null)
    ?.settingsFrom;
  return settingsFrom ?? location.pathname;
}

/** The deepest item whose `to` matches the URL. Ties (equal depth) go to the
 * later-visited item — hence `>=`, not `>` — so a child sharing its parent's
 * route wins, since children are visited after their parent. */
export function findActiveItem(
  items: MenuItem[],
  pathname: string,
): MenuItem | undefined {
  let best: MenuItem | undefined;
  const visit = (list: MenuItem[]) => {
    for (const item of list) {
      if (
        item.show !== false &&
        matchesRoute(item.to, pathname) &&
        (best === undefined || depth(item.to) >= depth(best.to))
      ) {
        best = item;
      }
      if (item.children) {
        visit(item.children);
      }
    }
  };
  visit(items);
  return best;
}

/** Whether the URL is at or below `to` (root is an ancestor of everything). */
export function isUnder(to: string, pathname: string): boolean {
  return to === "/" || pathname === to || pathname.startsWith(`${to}/`);
}

/** Whether a menu item stands for the URL: as isUnder, except that "/" counts
 * only on the list itself — as an ancestor it would claim every page outside
 * the menu, such as adding a patient. */
export function matchesRoute(to: string, pathname: string): boolean {
  return to === "/" ? pathname === "/" : isUnder(to, pathname);
}

/** Number of path segments in `to` (`/` is 0, `/patient/123` is 2). */
function depth(to: string): number {
  return to.split("/").filter(Boolean).length;
}

/**
 * Whether a focus event came from the keyboard rather than a pointer.
 *
 * Clicking a row focuses it, so opening the tooltip on any focus left it
 * hanging over the page after the click — visible until focus moved elsewhere.
 * `:focus-visible` is the browser's own answer to "did the user need a focus
 * indicator here", which is exactly the question. (Radix's own tooltip trigger
 * solves this by tracking pointerdown; these tooltips drive `open` themselves,
 * so they have to ask separately.)
 */
export function keyboardFocus(event: React.FocusEvent): boolean {
  return (
    event.target instanceof HTMLElement &&
    event.target.matches(":focus-visible")
  );
}

/**
 * Anchors the footer toggle's tooltip to the sidebar's edge.
 *
 * The toggle keeps its expanded width while collapsed — its column is pinned so
 * labels fade instead of re-flowing — so making it the Radix trigger puts the
 * tooltip ~200px to the right, as if the sidebar were still open. Narrowing it
 * fixes the position but breaks the fade, because the label's box then
 * collapses instantly instead of animating.
 *
 * So the trigger is this: an inert, zero-interaction span sized to the visible
 * strip, with the toggle driving `open` on hover and keyboard focus. Position
 * and animation both stay correct, and the toggle's own layout is untouched.
 */
export function NavTooltip({
  label,
  open,
  width,
}: {
  label: string;
  open: boolean;
  /** Distance from the element's left edge to the sidebar's edge. */
  width: string;
}) {
  return (
    <Tooltip
      open={open}
      content={label}
      side="right"
      sideOffset={NAV_TOOLTIP_OFFSET}
    >
      <span
        aria-hidden
        className={cn("pointer-events-none absolute inset-y-0 left-0", width)}
      />
    </Tooltip>
  );
}

/** Grace after the pointer leaves a row or its flyout, before the flyout
 * closes. */
const FLYOUT_CLOSE_DELAY = 150;

interface NavFlyoutController {
  openId: string | undefined;
  /** Hover and keyboard-focus handlers for a row; none while `enabled` is
   * false, i.e. while the row's label is readable. */
  triggerProps(
    id: string,
    enabled: boolean,
  ): Pick<
    React.HTMLAttributes<HTMLElement>,
    "onMouseEnter" | "onMouseLeave" | "onFocus" | "onBlur"
  >;
  close: (id: string) => void;
}

const NavFlyoutContext = createContext<NavFlyoutController | undefined>(
  undefined,
);

/**
 * One open flyout for the whole sidebar, shared by both layers, so the rail and
 * the level-2 strip hand off between each other the same way rows within one
 * of them do.
 */
export function NavFlyoutProvider({ children }: { children: React.ReactNode }) {
  const [openId, setOpenId] = useState<string>();
  const timer = useRef<number>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  // Another row's flyout takes over at once, rather than waiting for the open
  // one to close first: the hand-off is what makes running the pointer down
  // the rows read as smooth.
  const enter = (id: string) => {
    window.clearTimeout(timer.current);
    setOpenId(id);
  };
  const leave = () => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(
      () => setOpenId(undefined),
      FLYOUT_CLOSE_DELAY,
    );
  };
  // Only if it is still the open one: a dismissal can land after the pointer
  // has already reached another row, whose flyout must stay. Stable, as
  // NavFlyout calls it from an effect's cleanup.
  const close = useCallback(
    (id: string) =>
      setOpenId((current) => (current === id ? undefined : current)),
    [],
  );

  const controller: NavFlyoutController = {
    openId,
    triggerProps: (id, enabled) =>
      enabled
        ? {
            onMouseEnter: () => enter(id),
            onMouseLeave: leave,
            onFocus: (event) => {
              if (keyboardFocus(event)) enter(id);
            },
            onBlur: leave,
          }
        : {},
    close,
  };

  return (
    <NavFlyoutContext.Provider value={controller}>
      {children}
    </NavFlyoutContext.Provider>
  );
}

export function useNavFlyout(): NavFlyoutController {
  const controller = useContext(NavFlyoutContext);
  if (!controller) {
    throw new Error("useNavFlyout must be used within NavFlyoutProvider");
  }
  return controller;
}

/**
 * An icon-only row's label, as a menu: the row itself, then any pages indented
 * beneath it. Opens on hover beside the sidebar's edge, from the same inert
 * anchor NavTooltip uses, for the same reason.
 *
 * Rendered inside the row it belongs to, so React counts the portalled flyout
 * as part of that row: reaching it fires the row's own mouseenter, so the row's
 * handlers keep it open with no extra wiring. The close delay covers the
 * FLYOUT_OFFSET gap the pointer crosses on the way.
 */
export function NavFlyout({
  id,
  entries,
  width,
  rowHeight,
}: {
  id: string;
  entries: { item: MenuItem; current: boolean; nested?: boolean }[];
  /** Distance from the element's left edge to the sidebar's edge. */
  width: string;
  /** Height of the row it belongs to, to centre the flyout's first entry on it. */
  rowHeight: number;
}) {
  const flyout = useNavFlyout();

  // Gone while open — the sidebar expanded under the pointer, say — it would
  // stay the open one: its row drops its handlers along with it, so no leave
  // ever arrives, and the flyout would reopen on its own the next time the
  // labels hide.
  const { close } = flyout;
  useEffect(() => () => close(id), [close, id]);

  // Another row's flyout has opened while this one was still fading out. Two
  // panels at once, at different heights, read as a flicker, so the outgoing
  // one leaves at once instead of finishing its fade. Unmounted rather than
  // given `animate-none`: Radix waits for the exit animation's own end event,
  // which cutting the animation short never sends, and the panel would stay.
  const superseded = flyout.openId !== undefined && flyout.openId !== id;

  return (
    <Popover
      open={flyout.openId === id}
      onOpenChange={(open) => !open && flyout.close(id)}
    >
      <PopoverAnchor asChild>
        <span
          aria-hidden
          className={cn("pointer-events-none absolute inset-y-0 left-0", width)}
        />
      </PopoverAnchor>
      {!superseded && (
        <PopoverContent
          side="right"
          align="start"
          sideOffset={FLYOUT_OFFSET}
          // The content's 1px border and 4px padding sit above the first entry.
          alignOffset={rowHeight / 2 - (1 + 4 + FLYOUT_ENTRY_HEIGHT / 2)}
          // Focus stays on the sidebar row: the flyout opens on hover, and
          // pulling focus into it would scroll and announce on every pass.
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          // React bubbles through the portal, and the sidebar collapses on a
          // double-click of its empty surface — which this padding is not.
          onDoubleClick={(event) => event.stopPropagation()}
          // The popover's surface, but the sidebar's edge and corner radius, so
          // it reads as part of the sidebar rather than a generic popover.
          // Fade only: the popover's zoom and slide in from the left are reset,
          // with `!` because they sit behind the same `data-*` variants. The
          // sidebar's curve, a touch longer than the popover's 150ms.
          className="text-sidebar-foreground border-sidebar-edge w-56 rounded-[9px] p-1 duration-200 ease-[cubic-bezier(.4,0,.2,1)] data-[side=right]:slide-in-from-left-0! data-[state=closed]:zoom-out-100! data-[state=open]:zoom-in-100!"
        >
          <ul className="flex flex-col gap-px">
            {entries.map(({ item, current, nested }) => (
              <li key={`${item.to}:${item.label}`}>
                <Link
                  to={item.to}
                  aria-current={current ? "page" : undefined}
                  onClick={() => flyout.close(id)}
                  className={cn(
                    // The row and its pages share one design; only the indent
                    // tells them apart. Type, ink and hover are the level-2
                    // rows'.
                    "flex items-center rounded-[7px] pr-3 text-[13px] tracking-[-0.005em] whitespace-nowrap transition-colors duration-140 ease-[ease] hover:no-underline",
                    nested ? "pl-6" : "pl-3",
                    // Only picks out the current page among others: a lone
                    // entry is the row's label, and the row already shows it.
                    current && entries.length > 1
                      ? "bg-nav-active text-nav-active-foreground font-medium"
                      : "text-sidebar-foreground hover:bg-sidebar-accent",
                  )}
                  style={{ height: FLYOUT_ENTRY_HEIGHT }}
                >
                  <span className="min-w-0 flex-1 truncate">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </PopoverContent>
      )}
    </Popover>
  );
}

/** Gap between the sidebar's edge and a flyout. */
const FLYOUT_OFFSET = 6;

/** A flyout entry's height: the level-2 rows' 33px. */
const FLYOUT_ENTRY_HEIGHT = 33;

/**
 * An icon-only row's sign that it has pages, which its flyout lists. Sits
 * between the glyph and the column's edge at 48px, in the row's ink. Inert, so
 * the press falls through to the link underneath.
 */
export function IconOnlyChevron({
  active,
  className,
}: {
  /** The row is highlighted, so the arrow takes the active ink. */
  active: boolean;
  /** The glyph's size, which follows the row's. */
  className: string;
}) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute top-1/2 left-[31px] flex -translate-y-1/2"
    >
      <ChevronRightIcon
        className={cn(
          className,
          active ? "text-nav-active-foreground" : "text-nav-icon",
        )}
        strokeWidth={2.2}
      />
    </span>
  );
}
